import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal } from "./Reveal";

const MILESTONES = [
  { year: "1990", text: "CA Sandeep Arora qualified as a CA" },
  { year: "2015", text: "CA Gurbaksh Dang qualified as a CA" },
  { year: "2017", text: "Firm incorporation with ICAI" },
  { year: "2019", text: "Firm merger forms as a Partnership Firm" },
  { year: "2021", text: "Joined as CII Council — Income Tax Convener" },
  { year: "2022", text: "Qualification as Forensic Auditors" },
  { year: "2023", text: "CA Anmol Attry qualified as a CA" },
  { year: "2025", text: "CA Anmol Attry joins as a Partner" },
];

const EASE = [0.16, 1, 0.3, 1];

const Milestones = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  // Horizontal translation: travel = (n - 1) * column; we approximate with vw
  // Use 78% so the last item lands centred
  const x = useTransform(scrollYProgress, [0, 1], ["8vw", "-78%"]);

  // The progress line that "fills" as user scrolls
  const lineProgress = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  // Heading reveals at the top of section, then fades a touch as you scroll
  const headingY = useTransform(scrollYProgress, [0, 0.15], [0, -40]);
  const headingOpacity = useTransform(scrollYProgress, [0, 0.15], [1, 0.4]);

  return (
    <>
      {/* DESKTOP: scroll-pinned horizontal timeline */}
      <section
        ref={containerRef}
        data-testid="milestones-section"
        className="relative hidden md:block bg-neutral-950 text-neutral-50"
        style={{ height: "420vh" }}
        aria-label="Our milestones"
      >
        <div className="sticky top-0 h-screen overflow-hidden flex flex-col">
          {/* Header */}
          <motion.div
            style={{ y: headingY, opacity: headingOpacity }}
            className="px-6 md:px-12 pt-20 lg:pt-24"
          >
            <div className="max-w-[1400px] mx-auto flex items-end justify-between gap-6">
              <div>
                <p className="text-[11px] tracking-[0.32em] uppercase text-neutral-500 mb-4">
                  — A practice in motion
                </p>
                <h2 className="font-serif text-5xl lg:text-7xl font-light tracking-tighter leading-[0.95]">
                  Our <span className="italic">milestones.</span>
                </h2>
              </div>
              <p className="text-[11px] tracking-[0.32em] uppercase text-neutral-500 hidden lg:block">
                Scroll to traverse →
              </p>
            </div>
          </motion.div>

          {/* Horizontal track */}
          <div className="flex-1 relative flex items-center overflow-hidden">
            {/* The horizontal rule running through the middle */}
            <div className="absolute left-0 right-0 top-1/2 h-px bg-neutral-800" />
            {/* The progress fill */}
            <motion.div
              style={{ width: lineProgress }}
              className="absolute left-0 top-1/2 h-px bg-neutral-50 origin-left"
            />

            <motion.ul
              style={{ x }}
              className="flex gap-[18vw] items-center pl-0 list-none m-0"
            >
              {MILESTONES.map((m, i) => {
                const above = i % 2 === 0;
                return (
                  <li
                    key={m.year}
                    className="relative flex flex-col items-center shrink-0"
                    style={{ width: "32vw" }}
                  >
                    {above && (
                      <div className="absolute bottom-[calc(50%+24px)] left-1/2 -translate-x-1/2 text-center w-[28vw]">
                        <p className="font-serif text-[7vw] lg:text-[6.5vw] font-light leading-none tracking-tighter">
                          {m.year}
                        </p>
                        <p className="text-sm lg:text-base text-neutral-400 mt-4 leading-relaxed max-w-xs mx-auto">
                          {m.text}
                        </p>
                      </div>
                    )}

                    {/* Dot on the line */}
                    <div className="relative w-3 h-3 rounded-full bg-neutral-50 ring-4 ring-neutral-950" />

                    {!above && (
                      <div className="absolute top-[calc(50%+24px)] left-1/2 -translate-x-1/2 text-center w-[28vw]">
                        <p className="font-serif text-[7vw] lg:text-[6.5vw] font-light leading-none tracking-tighter">
                          {m.year}
                        </p>
                        <p className="text-sm lg:text-base text-neutral-400 mt-4 leading-relaxed max-w-xs mx-auto">
                          {m.text}
                        </p>
                      </div>
                    )}
                  </li>
                );
              })}
            </motion.ul>
          </div>

          {/* Footer hint */}
          <div className="px-6 md:px-12 pb-10 lg:pb-14">
            <div className="max-w-[1400px] mx-auto flex items-center justify-between text-[11px] tracking-[0.32em] uppercase text-neutral-500 border-t border-neutral-900 pt-6">
              <span>1990 — 2025</span>
              <span>{MILESTONES.length} milestones</span>
              <span className="hidden md:inline">Attry &amp; Associates · Chandigarh</span>
            </div>
          </div>
        </div>
      </section>

      {/* MOBILE: vertical timeline */}
      <section
        data-testid="milestones-section-mobile"
        className="md:hidden bg-neutral-950 text-neutral-50 py-20"
      >
        <div className="px-6">
          <p className="text-[10px] tracking-[0.32em] uppercase text-neutral-500 mb-4">
            — A practice in motion
          </p>
          <h2 className="font-serif text-5xl font-light tracking-tighter leading-[0.95] mb-14">
            Our <span className="italic">milestones.</span>
          </h2>

          <ol className="relative border-l border-neutral-800 pl-8 space-y-12 m-0">
            {MILESTONES.map((m, i) => (
              <motion.li
                key={m.year}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: i * 0.05, ease: EASE }}
                className="relative"
              >
                <span className="absolute -left-[37px] top-3 w-3 h-3 rounded-full bg-neutral-50 ring-4 ring-neutral-950" />
                <p className="font-serif text-5xl font-light leading-none tracking-tighter">
                  {m.year}
                </p>
                <p className="text-sm text-neutral-400 mt-3 leading-relaxed">{m.text}</p>
              </motion.li>
            ))}
          </ol>
        </div>
      </section>
    </>
  );
};

export default Milestones;
